<template>
  <div class="container-fluid px-4 mt-4">
    <div class="mb-3">
      <h5 class="fw-bold mb-0">공지사항</h5>
    </div>

    <!-- 로딩 -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">조회 중...</span>
      </div>
      <p class="mt-3 text-muted">공지사항을 로드 중입니다...</p>
    </div>

    <!-- 오류 -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <h5 class="alert-heading">오류 발생</h5>
      <p class="mb-0">{{ error }}</p>
    </div>

    <!-- 공지사항 목록 -->
    <div v-else>
      <div v-if="notices.length === 0" class="alert alert-info">
        <p class="mb-0">공지사항이 없습니다.</p>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th style="width: 60px;">#</th>
              <th>제목</th>
              <th class="text-center" style="width: 120px;">작성일</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="notice in notices" :key="notice.NoticeSeq" @click="openDetail(notice)" style="cursor: pointer;">
              <td class="text-muted small">{{ notice.NoticeSeq }}</td>
              <td class="fw-500">{{ notice.Title }}</td>
              <td class="text-center text-muted small">{{ formatDate(notice.CreatedDate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 페이지네이션 -->
      <nav v-if="totalPages > 1" class="mt-4" aria-label="Page navigation">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="goToPage(1)">처음</button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="goToPage(currentPage - 1)">이전</button>
          </li>

          <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage }">
            <button class="page-link" @click="goToPage(page)">{{ page }}</button>
          </li>

          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="goToPage(currentPage + 1)">다음</button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="goToPage(totalPages)">마지막</button>
          </li>
        </ul>
      </nav>
    </div>

    <!-- 상세 보기 모달 -->
    <div v-if="detailModal" class="modal-backdrop-custom">
      <div class="modal-box" @click.stop>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="fw-bold mb-0">{{ selectedNotice.Title }}</h6>
          <button type="button" class="btn-close" @click="closeDetail"></button>
        </div>

        <div class="border-bottom pb-2 mb-3">
          <small class="text-muted">{{ formatDate(selectedNotice.CreatedDate) }}</small>
        </div>

        <div class="notice-content">
          {{ selectedNotice.Content }}
        </div>

        <div class="d-flex gap-2 justify-content-end mt-4">
          <button type="button" class="btn btn-secondary btn-sm" @click="closeDetail">닫기</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'NoticeView',
  data() {
    return {
      loading: false,
      error: null,
      notices: [],
      currentPage: 1,
      totalCount: 0,
      totalPages: 1,
      pageSize: 10,
      detailModal: false,
      selectedNotice: {
        NoticeSeq: null,
        Title: '',
        Content: '',
        CreatedDate: null
      }
    }
  },
  computed: {
    visiblePages() {
      const maxVisible = 5;
      let startPage = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let endPage = Math.min(this.totalPages, startPage + maxVisible - 1);

      if (endPage - startPage + 1 < maxVisible) {
        startPage = Math.max(1, endPage - maxVisible + 1);
      }

      const pages = [];
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  mounted() {
    this.fetchNotices();
  },
  methods: {
    async fetchNotices() {
      this.loading = true;
      this.error = null;
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/notices`, {
          params: { page: this.currentPage }
        });

        if (res.data.success && res.data.notices) {
          this.notices = res.data.notices;
          this.totalCount = res.data.totalCount;
          this.totalPages = res.data.totalPages;
          this.currentPage = res.data.currentPage;
        } else {
          this.error = '공지사항을 불러올 수 없습니다.';
        }
      } catch (e) {
        console.error('fetchNotices error:', e);
        this.error = e.response?.data?.message || '공지사항 목록을 조회할 수 없습니다.';
      } finally {
        this.loading = false;
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.fetchNotices();
      window.scrollTo(0, 0);
    },
    async openDetail(notice) {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/notices/${notice.NoticeSeq}`);
        if (res.data.success && res.data.notice) {
          this.selectedNotice = res.data.notice;
          this.detailModal = true;
        }
      } catch (e) {
        console.error('openDetail error:', e);
        alert('공지사항을 불러올 수 없습니다.');
      }
    },
    closeDetail() {
      this.detailModal = false;
      this.selectedNotice = {
        NoticeSeq: null,
        Title: '',
        Content: '',
        CreatedDate: null
      };
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  }
}
</script>

<style scoped>
.table {
  margin-bottom: 0;
}

.table thead th {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
  padding: 0.75rem;
}

.table tbody tr {
  transition: background-color 0.15s ease;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}

.table tbody td {
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
}

.fw-500 {
  font-weight: 500;
}

.text-muted {
  color: #6b7280;
}

/* 페이지네이션 */
.pagination {
  margin-top: 2rem;
  margin-bottom: 2rem;
}

.page-link {
  color: #667eea;
  border-color: #dee2e6;
}

.page-link:hover {
  color: #667eea;
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.page-item.active .page-link {
  background-color: #667eea;
  border-color: #667eea;
}

.page-item.disabled .page-link {
  color: #6b7280;
  pointer-events: none;
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

/* 모달 스타일 */
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: white;
  border-radius: 6px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
}

.notice-content {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.6;
  color: #1a1a1a;
  font-size: 0.95rem;
}

.btn-close {
  padding: 0.25rem;
}
</style>
